import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth, browserPopupRedirectResolver } from '@/firebase';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
} from 'firebase/auth';
import { track } from '@/analytics';

// Set before a sign-in redirect so the returning page can log the login once.
const LOGIN_PENDING_KEY = 'loginPending';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const ready = ref(false);

  const isLoggedIn = computed(() => !!user.value);

  function _hydrateFromStorage() {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data && data.expirationTime > Date.now()) return data;
      localStorage.removeItem('userData');
    } catch {
      localStorage.removeItem('userData');
    }
    return null;
  }

  function _persistToStorage(firebaseUser) {
    const expirationTime = Date.now() + 7 * 24 * 60 * 60 * 1000;
    const data = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      expirationTime,
    };
    localStorage.setItem('userData', JSON.stringify(data));
    return data;
  }

  function init() {
    return new Promise((resolve) => {
      const cached = _hydrateFromStorage();
      if (cached) user.value = cached;

      onAuthStateChanged(
        auth,
        (firebaseUser) => {
          if (firebaseUser) {
            user.value = _persistToStorage(firebaseUser);
            if (sessionStorage.getItem(LOGIN_PENDING_KEY)) {
              sessionStorage.removeItem(LOGIN_PENDING_KEY);
              track('login', { method: 'google' });
            }
          } else if (!cached) {
            user.value = null;
            localStorage.removeItem('userData');
          }
          ready.value = true;
          resolve();
        },
        () => {
          // Firebase may throw internal assertion errors during
          // stale popup cleanup — resolve with cached data if available.
          ready.value = true;
          resolve();
        }
      );
    });
  }

  async function signIn() {
    const provider = new GoogleAuthProvider();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isStandalone || isMobile) {
      sessionStorage.setItem(LOGIN_PENDING_KEY, '1');
      await signInWithRedirect(
        auth,
        provider,
        browserPopupRedirectResolver
      );
    } else {
      const result = await signInWithPopup(
        auth,
        provider,
        browserPopupRedirectResolver
      );
      user.value = _persistToStorage(result.user);
      track('login', { method: 'google' });
    }
  }

  async function signOut() {
    await auth.signOut();
    user.value = null;
    localStorage.removeItem('userData');
  }

  return { user, ready, isLoggedIn, init, signIn, signOut };
});
